import { createFileRoute } from "@tanstack/react-router";
import {
	BarChart3,
	Bot,
	Briefcase,
	GitBranch,
	ShieldCheck,
	Workflow,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/for/b2b-saas";

export const Route = createFileRoute(path)({
	component: ForB2bSaas,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher for B2B SaaS - AI Social Media for Marketing Teams",
			description:
				"B2B SaaS teams need consistent LinkedIn and X presence. AgentReacher schedules launch posts, founder threads, and product updates from one workspace.",
		}),
});

function ForB2bSaas() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="For B2B SaaS"
				title="Turn every release into a pipeline-driving post."
				description="Drop a changelog into chat and get launch threads, founder posts, and follow-ups ready to approve. Show up consistently on LinkedIn and X without hiring a content lead."
			/>

			<MarketingSection
				title="What B2B teams get out of AgentReacher"
				description="Built for marketing leads, founder-led marketing, and product marketers."
			>
				<FeatureGrid
					features={[
						{
							icon: Briefcase,
							title: "LinkedIn-first",
							description:
								"LinkedIn is the highest-leverage channel for B2B. The agent treats it that way.",
						},
						{
							icon: Bot,
							title: "Founder voice + brand voice",
							description:
								"Train one agent on the founder, another on the brand. Both stay distinct.",
						},
						{
							icon: GitBranch,
							title: "Launch playbooks",
							description:
								"Pre-built sequences for product launches, funding announcements, and ARR milestones.",
						},
						{
							icon: ShieldCheck,
							title: "Approval gates",
							description:
								"Founder reviews. PMM approves. Comms signs off. Roles built into every plan.",
						},
						{
							icon: Workflow,
							title: "MCP for product builders",
							description:
								"Drive launches from Cursor or Claude. Diff in, thread out.",
						},
						{
							icon: BarChart3,
							title: "Performance tracking",
							description:
								"Track which threads drive trial signups. Iterate based on results.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="A typical SaaS workflow" align="left">
				<ol className="mx-auto max-w-2xl space-y-3 text-sm leading-relaxed text-muted-foreground">
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							1
						</span>
						<span>
							Connect founder LinkedIn, company LinkedIn, X, and Bluesky.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							2
						</span>
						<span>
							Train the agent on past launches and changelogs to learn the
							voice.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							3
						</span>
						<span>
							Set the cadence: weekly thread, daily insights, launch-day
							sequences.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							4
						</span>
						<span>
							PMM and founder approve. Posts ship on schedule. Track signups.
						</span>
					</li>
				</ol>
			</MarketingSection>

			<MarketingSection>
				<div className="mx-auto max-w-2xl space-y-4 text-center text-sm leading-relaxed text-muted-foreground">
					<p>
						Pro covers seed-stage teams. Max scales through Series B and beyond.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
